import React, {FC, useState} from 'react';
import AurChip, {FilterSelection} from './AurChip';
import {Typography} from '@mui/material';
import {ReactComponent as DownIcon} from '../../../assests/svg/chev_down.svg';
import {ReactComponent as UpIcon} from '../../../assests/svg/chev_up.svg';
import {ReactComponent as SaveIcon} from '../../../assests/svg/save.svg';
import {ReactComponent as ResetIcon} from '../../../assests/svg/reset.svg';
import AurButton from '../aur-button/AurButton';
import {t} from 'i18next';
import AurLoadingIndicator from '../aur-loading-indicator/AurLoadingIndictor';
import {
  GA_CLICK_TYPES,
  GA_EVENT_NAMES,
  GA_REGIONS,
} from '../../../constants/analyticsConstants';
import {useAnalytics} from '../../../hooks/useAnalytics';

export interface AurChipConfig {
  id?: number;
  icon?: React.ReactElement;
  filterName?: string;
  filterSelection: FilterSelection[];
  showRemove?: boolean;
  onRemove?: () => void;
}

export type AurChipListProps = {
  chipConfigs?: AurChipConfig[];
  hasFilterValueChanged?: boolean;
  onSave?: (chipConfigs?: AurChipConfig[]) => void;
  onClearAll?: () => void;
  onResetToDefault?: () => void;
  isOnSaveLoading?: boolean;
  isClearAllLoading?: boolean;
  saveButtonShow?: boolean;
  trackFilter?: boolean;
};

const AurChipList: FC<AurChipListProps> = ({
  chipConfigs,
  onClearAll,
  onSave,
  hasFilterValueChanged,
  onResetToDefault,
  isOnSaveLoading,
  isClearAllLoading,
  saveButtonShow = true,
  trackFilter = false,
}) => {
  const {trackGAEvent} = useAnalytics();

  const [showFilters, setShowFilters] = useState(true);
  const totalFilterSelectionCount = chipConfigs?.reduce((count, config) => {
    return count + (config.filterSelection?.length || 0);
  }, 0);

  return (
    <div className="chip-list-main-container">
      <div className="chip-list-buttons-section">
        <div className="chip-list-buttons-show-hide">
          <Typography className="typo-text body3_size selected-color">
            ({totalFilterSelectionCount}) {t('searchableDropdown.selected')}
          </Typography>
          {(chipConfigs?.reduce(
            (total, chip) => total + (chip.filterSelection?.length || 0),
            0
          ) || 0) > 0 ? (
            <AurButton
              ariaLabel={`${t('common.aurChip.clickTo')} ${showFilters ? 'Hide' : 'Show'} ${t('common.aurChip.filterChips')}`}
              title={`${showFilters ? 'Hide' : 'Show'} ${t('common.aurChip.filters')}`}
              classNames="chip-dropdown-button-icon"
              endIcon={showFilters ? <UpIcon aria-hidden="true" /> : <DownIcon aria-hidden="true" />}
              type="text"
              size="Small"
              onClick={() => {
                setShowFilters(!showFilters);
                trackFilter &&
                  trackGAEvent({
                    eventName: GA_EVENT_NAMES.FILTER,
                    click: {
                      name: `${showFilters ? t('gaTracking.global.hide') : t('gaTracking.global.show')} ${t('gaTracking.global.filter')}`,
                      type: GA_CLICK_TYPES.BUTTON,
                      region: GA_REGIONS.BODY,
                      action: t('gaTracking.reports.filterReport'),
                    },
                  });
              }}
              dataTestId="hide-show-filter-button"
            >
              {showFilters ? t('common.hide') : t('common.show')}{' '}
              {t('common.filter')}
            </AurButton>
          ) : (
            ''
          )}
        </div>
        {hasFilterValueChanged ? (
          <div className="chip-list-buttons-save">
            {saveButtonShow && (
              <AurButton
                classNames="chip-save-button-icon"
                startIcon={
                  isOnSaveLoading ? (
                    <AurLoadingIndicator size={16} className="progress-icon" />
                  ) : (
                    <SaveIcon aria-hidden="true" />
                  )
                }
                type="text"
                size="Small"
                ariaLabel={`${t('common.aurChip.saveAreaLabel')}`}
                title={`${t('common.saveFilters')}`}
                onClick={() => {
                  onSave && onSave(chipConfigs);
                  trackFilter &&
                    trackGAEvent({
                      eventName: GA_EVENT_NAMES.FILTER,
                      click: {
                        name: t('gaTracking.global.saveFilters'),
                        type: GA_CLICK_TYPES.BUTTON,
                        region: GA_REGIONS.BODY,
                        action: t('gaTracking.reports.filterReport'),
                      },
                    });
                }}
                dataTestId="save-filters"
              >
                {t('common.saveFilters')}
              </AurButton>
            )}
            <AurButton
              type="text"
              size="Small"
              onClick={onClearAll}
              ariaLabel={`${t('common.aurChip.clearAriaLabel')}`}
              title={`${t('common.clearFilters')}`}
              dataTestId="clear-filters"
              startIcon={
                isClearAllLoading && (
                  <AurLoadingIndicator size={16} className="progress-icon" />
                )
              }
            >
              {t('common.clearFilters')}
            </AurButton>
          </div>
        ) : (
          <div className="chip-list-buttons-save">
            <AurButton
              classNames="chip-reset-button-icon"
              startIcon={<ResetIcon aria-hidden="true" />}
              type="text"
              size="Small"
              onClick={onResetToDefault}
              dataTestId="Reset to Default"
            >
              {t('common.resetToDefault')}
            </AurButton>
          </div>
        )}
      </div>
      {/* <React.Fragment> */}
      <div className="chip-list-main-section">
        {showFilters &&
          chipConfigs?.map(
            (config) =>
              config?.filterSelection?.length > 0 && (
                <AurChip
                  key={config.id}
                  icon={config.icon}
                  filterName={config.filterName}
                  filterSelection={config.filterSelection}
                  showRemove={config.showRemove}
                  onRemove={config.onRemove}
                />
              )
          )}
        {/* </React.Fragment> */}
      </div>
    </div>
  );
};

export default AurChipList;
